import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { CustomTypography } from "../../../../../components/CustomTypography/CustomTypography";
import { CustomButton } from "../../../../../components/CustomButton/CustomButton";
import { IconWithBackground } from "../../../../../components/IconWithBackground/IconWithBackground";
import { IconButton } from "@mui/material";
import { Icon } from "../../../../../components/Icon/Icon";
import { colors } from "../../../../../styles/colors";
import { ModalCarteAndBadge } from "../../../../../containers/ModalCarteAndBadge/ModalCarteAndBadge";
import { ChangeEvent, FC, useState } from "react";
import {
  typeOfCardOrBadge,
  useAssignCardOrBadge,
  useGetAllCardsAndBadgeFleetByEmployee,
  useGetAllCardsFleetUnassigned,
} from "../../../../../api/badge";
import { getCurrentFleetId } from "../../../../../utils/getCurrentFleet";
import { CustomSearch } from "../../../../../components/CustomSearch/CustomSearch";
import toast from "react-hot-toast";
import { CustomTextDatePicker } from "../../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import dayjs from "dayjs";

type Props = {
  currentEmployee: TEmployeeWithoutVehicle | null;
};

export const Card: FC<Props> = ({ currentEmployee }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [startDate, setStartDate] = useState<
    string | number | Date | dayjs.Dayjs | null
  >(null);
  const [endDate, setEndDate] = useState<
    string | number | Date | dayjs.Dayjs | null
  >(null);
  const [selectedCard, setSelectedCard] = useState<TCard | null>(null);

  const onSuccess = () => {
    setSelectedCard(null);
    setStartDate(null);
    setEndDate(null);
  };

  const { cards } = useGetAllCardsFleetUnassigned(getCurrentFleetId());
  const { cardsAndBadges } = useGetAllCardsAndBadgeFleetByEmployee(
    getCurrentFleetId(),
    currentEmployee?.id ?? 0
  );
  const cardsForCurrentEmployee = cardsAndBadges.filter(
    (cards) =>
      cards.fleet_card.fleet_card_type.support_type === typeOfCardOrBadge.card
  );

  const { mutate: assignCard } = useAssignCardOrBadge(
    getCurrentFleetId(),
    currentEmployee?.id ?? 0,
    onSuccess
  );

  const [open, setOpen] = useState(false);

  const onChangeTextField = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.trim().length === 0) {
      setSelectedCard(null);
    }
  };

  const onChangeAutocomplet = (_: any, valueTrue: any) => {
    setSelectedCard(valueTrue as TCard);
  };

  const onClose = () => {
    setOpen(false);
  };

  const assignCardButton = () => {
    if (currentEmployee === null) {
      toast.error("Veuillez sélectionner un employé");
      return;
    } else {
      if (startDate === null || selectedCard === null) {
        toast.error(t("required.fieldRequired", "Fields (*) are required."));
      } else {
        assignCard({
          badgeOrCardId: selectedCard.id,
          company_user_id: currentEmployee.id,
          start_date: startDate,
          end_date: endDate,
        });
      }
    }
  };

  return (
    <>
      <ModalCarteAndBadge
        open={open}
        currentCartOrBadge={null}
        supportTypes={typeOfCardOrBadge.card}
        title={t("collaborator.newCard", "New card")}
        onCancel={onClose}
        onClose={onClose}
      />
      <div className={classes.divContentCard}>
        <div className={classes.divAdd}>
          <CustomTypography
            text={t("collaborator.cards", "Cards")}
            variant={"h4"}
            isUppercase
          />
          {/* <CustomIconButton
            iconName={"plus"}
            widthIcon={"30px"}
            heightIcon={"30px"}
            onClick={() => setOpen(true)}
          /> */}
        </div>
        <CustomSearch
          label={`${t("contrat.number", "Card number")} *`}
          placeholder={"123456789"}
          options={cards}
          onChange={onChangeAutocomplet}
          onChangeTextField={onChangeTextField}
          getOptionLabel={(card: any) =>
            `${card?.number}  - ${card?.cardBrand}` ?? ""
          }
        />
        <CustomTextDatePicker
          label={`${t("common.startDate", "Start date")} *`}
          value={startDate as any}
          onChange={(value) => setStartDate(dayjs(value).format("YYYY-MM-DD"))}
        />
        <CustomTextDatePicker
          label={`${t("common.endDate", "End date")}`}
          value={endDate as any}
          onChange={(value) => setEndDate(dayjs(value).format("YYYY-MM-DD"))}
        />
        <CustomButton
          onClick={assignCardButton}
          label={t("collaborator.addCard", "Add card")}
          variant={"primary"}
          width={"fit-content"}
          height={"40px"}
        />

        <div>
          <CustomTypography
            text={`${t("collaborator.allCards", "All cards")} :`}
            variant={"h4"}
            isUppercase
          />
          <div className={classes.divContainerAllCards}>
            {cardsForCurrentEmployee.map((card) => (
              <div className={classes.subContainerCard} key={card.id}>
                <div className={classes.subContainer2}>
                  <IconWithBackground iconName={"cards"} />
                  <div>
                    <CustomTypography
                      text={String(card.fleet_card.number)}
                      isUppercase
                      variant="link"
                      fontWeight={"bold"}
                    />
                    <CustomTypography
                      text={`${card.fleet_card.fleet_card_type.type} - ${card.fleet_card.fleet_card_type.marque} <br/> ${card.fleet_card.release_date}`}
                      variant="link"
                    />
                  </div>
                </div>
                <IconButton
                  aria-label={t("a11y.delete", "Delete")} className={classes.iconButton}>
                  <Icon
                    name="trash"
                    width={"24px"}
                    height={"24px"}
                    color={colors.error}
                  />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
